import React, { useState, useEffect } from 'react';
import { Tag, X, Plus } from 'lucide-react';
import { collection, getDocs, query } from 'firebase/firestore';
import { db } from '../../firebase';
import './TagSelector.css';

/**
 * TagSelector component for selecting and filtering content by tags
 * 
 * @param {Object} props
 * @param {Array} props.selectedTags - Array of currently selected tags
 * @param {Function} props.onTagToggle - Callback when a tag is toggled (added/removed)
 * @param {Array} props.availableTags - Optional predefined list of available tags
 * @param {boolean} props.allowCustom - Whether to allow adding custom tags
 * @param {boolean} props.showPopular - Whether to show popular tags section
 * @param {string} props.placeholder - Placeholder text for tag input
 */
function TagSelector({ 
  selectedTags = [], 
  onTagToggle, 
  availableTags = null,
  allowCustom = false,
  showPopular = true,
  placeholder = "Filter by tags..."
}) {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tagInput, setTagInput] = useState('');
  const [filteredTags, setFilteredTags] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Fetch tags from database if availableTags not provided
  useEffect(() => {
    if (availableTags) {
      setTags(availableTags);
      setLoading(false);
      return;
    }

    const fetchTags = async () => {
      try {
        setLoading(true);
        const tagsMap = new Map();
        
        // Get tags from the queries collection
        const queriesRef = collection(db, "queries");
        const queriesSnap = await getDocs(query(queriesRef));
        
        queriesSnap.forEach(doc => {
          const queryData = doc.data();
          if (queryData.tags && Array.isArray(queryData.tags)) {
            queryData.tags.forEach(tag => {
              if (tagsMap.has(tag)) {
                tagsMap.set(tag, tagsMap.get(tag) + 1);
              } else {
                tagsMap.set(tag, 1);
              }
            });
          }
        });
        
        // Convert to array and sort by popularity
        const tagsArray = Array.from(tagsMap.entries())
          .map(([tag, count]) => ({ tag, count }))
          .sort((a, b) => b.count - a.count);
        
        setTags(tagsArray);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching tags:", error);
        setLoading(false);
      }
    };
    
    fetchTags();
  }, [availableTags]);

  // Update filtered tags when tag input changes
  useEffect(() => {
    if (!tagInput.trim()) {
      setFilteredTags([]);
      return;
    }
    
    const normalizedInput = tagInput.toLowerCase().trim();
    const filtered = Array.isArray(tags[0]) 
      ? tags
          .filter(([tag]) => tag.toLowerCase().includes(normalizedInput))
          .slice(0, 10)
      : tags
          .filter(tag => 
            typeof tag === 'string' 
              ? tag.toLowerCase().includes(normalizedInput)
              : tag.tag.toLowerCase().includes(normalizedInput)
          )
          .slice(0, 10);
    
    setFilteredTags(filtered);
  }, [tagInput, tags]);

  const handleTagToggle = (tag) => {
    if (typeof onTagToggle === 'function') {
      const tagValue = typeof tag === 'string' ? tag : tag.tag;
      onTagToggle(tagValue);
    }
  };

  const handleInputChange = (e) => {
    setTagInput(e.target.value);
    setShowSuggestions(true);
  };

  const handleKeyDown = (e) => {
    // Add tag on Enter
    if (e.key === 'Enter' && tagInput.trim() && allowCustom) {
      e.preventDefault();
      const newTag = tagInput.trim();
      if (!selectedTags.includes(newTag)) {
        handleTagToggle(newTag);
      }
      setTagInput('');
    }
  };

  const handleAddCustomTag = () => {
    if (tagInput.trim() && allowCustom) {
      const newTag = tagInput.trim();
      if (!selectedTags.includes(newTag)) {
        handleTagToggle(newTag);
      }
      setTagInput('');
    }
  };

  const getPopularTags = () => {
    if (!showPopular || !tags.length) return [];
    
    // If tags is array of objects with tag and count
    if (typeof tags[0] === 'object' && tags[0].tag) {
      return tags.slice(0, 10); // Top 10 tags
    }
    
    // If tags is array of [tag, count] tuples
    if (Array.isArray(tags[0])) {
      return tags.slice(0, 10); // Top 10 tags
    }
    
    // If tags is just an array of strings
    return tags.slice(0, 10).map(tag => ({ tag, count: 1 }));
  };

  return (
    <div className="tag-selector">
      <div className="tag-input-container">
        <Tag size={16} className="tag-icon" />
        <input
          type="text"
          value={tagInput}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="tag-input"
          onFocus={() => setShowSuggestions(true)}
        />
        {allowCustom && tagInput.trim() && (
          <button 
            className="add-tag-btn" 
            onClick={handleAddCustomTag}
            title="Add as custom tag"
          >
            <Plus size={16} />
          </button>
        )}
      </div>

      {/* Selected Tags */}
      {selectedTags.length > 0 && (
        <div className="selected-tags">
          {selectedTags.map((tag, index) => (
            <div key={index} className="selected-tag">
              <span>{tag}</span>
              <button 
                className="remove-tag-btn" 
                onClick={() => handleTagToggle(tag)}
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Tag Suggestions */}
      {showSuggestions && (filteredTags.length > 0 || (showPopular && getPopularTags().length > 0)) && (
        <div className="tag-suggestions">
          {filteredTags.length > 0 ? (
            <div className="suggestion-group">
              <div className="suggestion-header">Matching Tags</div>
              <div className="suggestion-list">
                {filteredTags.map((tag, index) => {
                  const tagValue = typeof tag === 'string' ? tag : tag.tag;
                  const tagCount = typeof tag === 'object' ? tag.count : null;
                  
                  return (
                    <div 
                      key={index} 
                      className={`suggestion-tag ${selectedTags.includes(tagValue) ? 'selected' : ''}`}
                      onClick={() => handleTagToggle(tag)}
                    >
                      <span>{tagValue}</span>
                      {tagCount && <span className="tag-count">{tagCount}</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : showPopular && (
            <div className="suggestion-group">
              <div className="suggestion-header">Popular Tags</div>
              <div className="suggestion-list">
                {getPopularTags().map((tag, index) => {
                  const tagValue = typeof tag === 'string' ? tag : tag.tag;
                  const tagCount = typeof tag === 'object' ? tag.count : null;
                  
                  return (
                    <div 
                      key={index} 
                      className={`suggestion-tag ${selectedTags.includes(tagValue) ? 'selected' : ''}`}
                      onClick={() => handleTagToggle(tag)}
                    >
                      <span>{tagValue}</span>
                      {tagCount && <span className="tag-count">{tagCount}</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {loading && <div className="tags-loading">Loading tags...</div>}
    </div>
  );
}

export default TagSelector; 